"use client";

import {
    adminCreateArtifact,
    AdminCreateArtifactData,
    adminDeleteArtifact,
    AdminDeleteArtifactData,
    adminGetArtifact,
    AdminGetArtifactData,
    adminGetTags,
    adminUpdateArtifact,
    AdminUpdateArtifactData,

} from "@/api";
import { artifact } from "@/app/typing/artifacts";
import { usePathParam, useLocalNumberList } from "@/components/path-params";
import { DefEdit, EntryItem, useRefState } from "@/components/UI/tools";
import { Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { tag_list_item } from "@/app/typing/tags";
import { AppState, appState } from "@/app/appState";
import { observer } from "mobx-react-lite";
import { Request } from "@/components/CRUD";
import { PageBox } from "@/components/UI/page";
import { makeAutoObservable, runInAction } from "mobx";
import { editRequestWebSocket } from "@/aawebsocket/websocket.gen";
import { Org, User } from "@/components/UI/svgs";
import { shortenLabel } from "../chat/chatObjectEditorWindow/chatObjectEditorWindow";
import { decodeSource, encodeSource, sourceOptions } from "../chat/artifactSource";

const ArtifactEntry = observer(({ id, setId }: { id?: number; setId: (id?: number) => void }) => {
    const [refresh, setRefresh] = useState(0);
    const [loading, setLoading] = useState(false);

    const [artifact, setArtifact] = useState<artifact | undefined>(undefined);
    const [deleting, setDeleting] = useState(false);
    const [lock, setLock] = useRefState(false);

    const [edit, setEdit] = useState(false)
    const [variableName, setVariableName] = useRefState<string>("");
    const [content, setContent] = useRefState<string | undefined>("");
    const [tagIds, setTagIds] = useRefState<number[]>([]);
    // Encoded source id: positive is an event, negative a document. See artifactSource.
    const [sourceId, setSourceId] = useRefState<number | undefined>(undefined);
    const [tagSearch, setTagSearch] = useState<string>("")
    const [tagPageNumber, setTagPageNumber] = useState<number>(0)

    const [sourceSearch, setSourceSearch] = useState<string>("")
    const [sourcePageNumber, setSourcePageNumber] = useState<number>(0)

    const reloadTags = async () => {
        await appState.getTags({ pageNumber: tagPageNumber, search: tagSearch })
    };

    // Both source kinds feed one picker, so they page and search together.
    const reloadSources = async () => {
        await appState.getEvents({ pageNumber: sourcePageNumber, search: sourceSearch })
        await appState.getDocuments({ pageNumber: sourcePageNumber, search: sourceSearch })
    };

    useEffect(() => {

        reloadTags()
    }, [tagPageNumber, tagSearch])

    useEffect(() => {
        reloadSources()
    }, [sourcePageNumber, sourceSearch])

    const [ids, setIds] = useLocalNumberList("artifact_ids");



    const createArtifact = Request<AdminCreateArtifactData, any>(
        adminCreateArtifact,
        () => ({
            body: {
                tag_ids: tagIds.current,
                variable_name: variableName.current,
                content: content.current!,
                ...decodeSource(sourceId.current),
            },
        }),
        setLoading,
        (data: any) => {
            setId(data?.artifact_id)
            appState.getArtifacts({ pageNumber: 0, search: "" });
        },
        (error) => {
            appState.setModal({
                title: appState.t("common.error"),
                body: appState.t("objects.errorCreateArtifact"),
                comment: { name: appState.t("common.detail"), val: error.detail?.toString() ?? "" },
            })
        },
        () => variableName.current.length > 0 && content.current != undefined && content.current.length > 0,
    )

    const deleteArtifact = Request<AdminDeleteArtifactData, any>(
        adminDeleteArtifact,
        () => ({
            body: {
                artifact_id: id!
            },
        }),
        setDeleting,
        (data: any) => {
            appState.getArtifacts({ pageNumber: 0, search: "" });
            setId(undefined);
        },
        (error) => {
            appState.setModal({ title: appState.t("common.error"), body: appState.t("objects.errorDeleteArtifact") });
        },
        () => id != undefined && id > 0,
    )


    const updateArtifact = Request<AdminUpdateArtifactData, any>(
        adminUpdateArtifact,
        () => ({
            body: {
                id: id!,
                tag_ids: tagIds.current,
                variable_name: variableName.current,
                content: content.current!,
                ...decodeSource(sourceId.current),
            },
        }),
        setLoading,
        (data) => { appState.getArtifacts({ pageNumber: 0, search: "" }); },
        (error) => {
            setRefresh(p => p + 1)
            setEdit(false)
            appState.setModal({
                title: appState.t("common.error"),
                body: appState.t("objects.errorUpdateArtifact"),
                comment: { name: appState.t("common.detail"), val: error.detail?.toString() ?? "" },
            })
        },
        () => id != undefined && id > 0 && variableName.current.length > 0 && content.current != undefined && content.current.length > 0,
    )

    const getArtifact = Request<AdminGetArtifactData, any>(
        adminGetArtifact,
        () => ({
            body: {
                id: id!
            },
        }),
        setLoading,
        (data: any) => {
            setArtifact(data.artifact);
        },
        (error) => {
            setArtifact(undefined)
            appState.setModal({ title: appState.t("common.error"), body: appState.t("objects.errorGetArtifact") });
        },
        () => id != undefined && id > 0,
    )


    useEffect(() => {

        setArtifact(undefined);
        if (id && id > 0)
            getArtifact();


    }, [id]);

    const refreshRefs = () => {
        if (artifact) {
            setLock(artifact.user_lock ? !(artifact.user_lock == appState.user?.id) : false)
            setVariableName(artifact.variable_name);
            setContent(artifact.content);
            setTagIds(artifact.tag_ids ?? []);
            setSourceId(encodeSource(artifact));
        } else {
            setVariableName("");
            setContent("");
            setTagIds([]);
            setSourceId(undefined);
        }
    };

    useEffect(() => {

        refreshRefs();
        setRefresh((prev) => prev + 1);
    }, [artifact]);



    return (
        <PageBox>
            <div id="artifact" className="h-full w-full">
                {
                    (artifact && artifact.id == id) || id == -1 ?

                        <>
                        <EntryItem
                            lock={lock.current}
                            setEdit={(val: boolean) => DefEdit(val, id, setEdit, "artifact")}
                            edit={edit}

                            name={appState.t("objects.list.artifact")}
                            loading={loading}
                            deleting={deleting}
                            readonly={(![4, 5, 6, 7].includes(appState.user?.active_organisation.permission_type ?? 0) || ![4, 5, 6, 7].includes(artifact?.permission_type ?? 0)) && id != -1}

                            createEntry={createArtifact}
                            deleteEntry={deleteArtifact}
                            updateEntry={updateArtifact}
                            refreshCount={refresh}
                            idParams={{
                                singular: { get: id, set: setId },
                                plural: { get: ids, set: setIds },
                            }}
                            refColumns={[
                                {
                                    type: "richtext",
                                    label: 'Content (To reference artifacts, use their variable name in the following format: ${variable_name, "a fallback value"})',
                                    validator: (value: string | undefined) => (value ? true : false),
                                    value: content.current,
                                    onChange: setContent,
                                    artifactsComplete: true,
                                },
                            ]}
                            nameColumn={{
                                type: "text",
                                value: variableName.current,
                                onChange: setVariableName,
                                validator: (val: string) => !/["'${}`,]/.test(val),
                                label: appState.t("objects.variableName"),
                            }}
                            stateColumns={[
                                {
                                    label: "#",
                                    type: "multiple",
                                    onChange: setTagIds,
                                    search: { get: tagSearch, set: setTagSearch },
                                    pageNumber: { get: tagPageNumber, set: setTagPageNumber },
                                    value: tagIds.current,
                                    items: appState.tags.filter(x => [2, 3, 6, 7].includes(x.effective_permission_type))?.map((x) => {
                                        return { id: x.id, name: x.name, children: (<div className="flex flex-row gap-[0.5rem] items-center"><>{shortenLabel(x.name)}</>{x.scope == 'me' ? <User size={16} /> : <Org size={16} />}</div>), defaultValue: tagIds.current?.includes(x.id) };
                                    }),
                                    validator: (val: number[]) => val.length > 0,
                                },
                                {
                                    label: appState.t("objects.source"),
                                    type: "single",
                                    onChange: setSourceId,
                                    search: { get: sourceSearch, set: setSourceSearch },
                                    pageNumber: { get: sourcePageNumber, set: setSourcePageNumber },
                                    value: sourceId.current,
                                    items: sourceOptions(appState.events, appState.documents, undefined).map((x) => ({
                                        id: x.id,
                                        name: x.name,
                                        children: (
                                            <div className="flex flex-row gap-[0.5rem] items-center">
                                                <span className="opacity-50">{appState.t(x.type === "document" ? "objects.list.document" : "objects.list.event")}</span>
                                                <>{shortenLabel(x.name)}</>
                                            </div>
                                        ),
                                    })),
                                    validator: () => true,
                                },
                            ]}
                        />
                        </>

                        :

                        null
                }
            </div>
        </PageBox>
    );
});

export default ArtifactEntry;
